import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
	Box,
	Typography,
	useTheme,
} from '@mui/material';
import dayjs from 'dayjs';
import { useSnackbar } from 'notistack';
import {
	ACTION_TYPES,
	useAppContext,
	useFeature,
} from '../../Context/AppContext';
import { deleteJobAssignment } from '../../Shared/Api';
import { markProOnboardingStepSeen } from '../../Shared/onboarding';
import { surfaceCard } from '../../Shared/surfaceCard';
import AssignmentModal from './AssignmentModal';
import {
	CrewWeekBoard,
	JobsWeekBoard,
} from './components/CrewWeekBoard';
import ScheduleToolbar from './components/ScheduleToolbar';
import TodayBoard from './components/TodayBoard';
import useScheduleBoard, { mondayOf } from './hooks/useScheduleBoard';

const SchedulePage = () => {
	const theme = useTheme();
	const navigate = useNavigate();
	const { state, dispatch } = useAppContext();
	const { enqueueSnackbar } = useSnackbar();
	const hasScheduling = useFeature('scheduling');
	const isManager =
		state.user.role === 'Manager' || Boolean(state.user.isSuperuser);

	const [preset, setPreset] = useState('crew_week');
	const [anchorDate, setAnchorDate] = useState(() => mondayOf(dayjs()));
	const [modalOpen, setModalOpen] = useState(false);
	const [editing, setEditing] = useState(null);
	const [defaults, setDefaults] = useState(null);

	const { board, loading, range, reload } = useScheduleBoard({
		hasScheduling,
		preset,
		anchorDate,
	});

	useEffect(() => {
		dispatch({
			type: ACTION_TYPES.SET_PAGE_TITLE,
			payload: { pageTitle: 'Schedule' },
		});
		return () => {
			dispatch({
				type: ACTION_TYPES.SET_PAGE_TITLE,
				payload: { pageTitle: '' },
			});
		};
	}, [dispatch]);

	useEffect(() => {
		if (isManager && hasScheduling) {
			markProOnboardingStepSeen('schedule');
		}
	}, [isManager, hasScheduling]);

	const openCreate = (prefill = null) => {
		setEditing(null);
		setDefaults(prefill);
		setModalOpen(true);
	};

	const openEdit = (assignment) => {
		if (!isManager) return;
		setDefaults(null);
		setEditing(assignment);
		setModalOpen(true);
	};

	const handleCellCreate = ({ row, date }) => {
		if (!isManager) return;
		const day = dayjs(date);
		const prefill = {
			starts_at: day.hour(9).minute(0).second(0),
			ends_at: day.hour(13).minute(0).second(0),
		};
		if (row?.type === 'user' && row.user_id) {
			prefill.user = {
				id: row.user_id,
				display_name: row.label,
			};
		}
		if (row?.type === 'job' && row.job_id) {
			prefill.job = {
				id: row.job_id,
				name: row.label,
				customer_name: row.customer_name,
			};
		}
		openCreate(prefill);
	};

	const handleDueClick = (item) => {
		if (!isManager || !item?.job_id) {
			return;
		}
		navigate(`/jobs/view/${item.job_id}`);
	};

	const handleDelete = async (assignment) => {
		if (!isManager) return;
		try {
			await deleteJobAssignment(assignment.id);
			enqueueSnackbar('Assignment deleted.', { variant: 'success' });
			reload();
		} catch (error) {
			enqueueSnackbar(error.message || 'Failed to delete assignment.', {
				variant: 'error',
			});
		}
	};

	if (!hasScheduling) {
		return (
			<Box sx={{ ...surfaceCard(theme), p: 3 }}>
				<Typography color="text.secondary">
					Scheduling is available on the Pro plan.
				</Typography>
			</Box>
		);
	}

	return (
		<Box sx={{ width: '100%', maxWidth: 1400, mx: 'auto' }}>
			<ScheduleToolbar
				preset={preset}
				onPresetChange={(next) => {
					setPreset(next);
					if (next === 'today') {
						setAnchorDate(dayjs());
					} else {
						setAnchorDate(mondayOf(dayjs()));
					}
				}}
				anchorDate={anchorDate}
				onAnchorChange={setAnchorDate}
				range={range}
				isManager={isManager}
				onCreate={() => openCreate(null)}
			/>

			{preset === 'today' ? (
				<TodayBoard
					board={board}
					loading={loading}
					isManager={isManager}
					onCreateForUser={handleCellCreate}
					onEditAssignment={openEdit}
					onDueClick={handleDueClick}
				/>
			) : preset === 'jobs_week' ? (
				<JobsWeekBoard
					board={board}
					loading={loading}
					isManager={isManager}
					onCellCreate={handleCellCreate}
					onEditAssignment={openEdit}
					onDueClick={handleDueClick}
				/>
			) : (
				<CrewWeekBoard
					board={board}
					loading={loading}
					isManager={isManager}
					onCellCreate={handleCellCreate}
					onEditAssignment={openEdit}
					onDueClick={handleDueClick}
				/>
			)}

			{isManager && (
				<AssignmentModal
					open={modalOpen}
					onClose={() => {
						setModalOpen(false);
						setEditing(null);
						setDefaults(null);
					}}
					assignment={editing}
					defaults={defaults}
					onSaved={reload}
					onDelete={
						editing
							? async () => {
									await handleDelete(editing);
									setModalOpen(false);
									setEditing(null);
								}
							: undefined
					}
				/>
			)}
		</Box>
	);
};

export default SchedulePage;
